import {createNextState} from '@reduxjs/toolkit';

type TValue<T, ItemId> = {id: ItemId} & T;
type TTable<Keys extends string, ItemId> = Record<Keys, TValue<any, ItemId>[]>;

const createDatabase = <TableKeys extends string, ItemId = number>(
  defaultData: TTable<TableKeys, ItemId>,
) => {
  let data: TTable<TableKeys, ItemId> = defaultData;
  const jobIds: number[] = [];

  return {
    table: <T = Record<string, any>>(key: TableKeys) => {
      const table = {
        load(values: TValue<T, ItemId>[]) {
          data[key] = values;
        },
        values(): TValue<T, ItemId>[] {
          return data[key];
        },
        push(value: TValue<T, ItemId>) {
          data[key].push(value);
        },
        get(id: ItemId): TValue<T, ItemId> | undefined {
          return data[key].find((value) => value.id === id) ?? undefined;
        },
        update(id: ItemId, mutation: (value: TValue<T, ItemId>) => TValue<T, ItemId> | void) {
          const targetValue = table.get(id);

          if (targetValue) {
            table.put(id, createNextState(targetValue, mutation));
          }
        },
        put(id: ItemId, nextValue: TValue<T, ItemId>) {
          data[key] = data[key].map((value) => {
            if (value.id === id) {
              return nextValue;
            }

            return value;
          });
        },
        delete(id: ItemId) {
          data[key] = data[key].filter((item) => item.id !== id);
        },
      };

      return table;
    },
    drop() {
      data = defaultData;
      jobIds.forEach((jobId) => clearInterval(jobId));
    },
    scheduleJob(job: (stopJob: () => void) => void, interval: number) {
      const intervalId: any = setInterval(() => job(() => clearInterval(intervalId)), interval);

      jobIds.push(intervalId);

      return intervalId;
    },
  };
};

// prettier-ignore
type TDatabase = ReturnType<typeof createDatabase<any, any>>;

export type {TDatabase}
export {createDatabase};
